import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../../convex/_generated/api';
import { ArrowLeft } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Switch from '../../components/Switch';
import { useAdmin } from '../../contexts/AdminContext';
import { isValidGmail, getPrivilegeLabel } from '../../utils/helpers';
import toast from 'react-hot-toast';

const PRIVILEGES = [
    'user_management',
    'content_moderation',
    'verification_approval',
    'analytics_view',
    'system_config',
    'basketball_management',
];

export default function AddAdmin() {
    const navigate = useNavigate();
    const { user } = useUser();
    const { admin } = useAdmin();
    const [email, setEmail] = useState('');
    const [selectedPrivileges, setSelectedPrivileges] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createAdmin = useMutation(api.platformAdmins.createAdmin);

    const togglePrivilege = (privilege) => {
        setSelectedPrivileges((prev) =>
            prev.includes(privilege)
                ? prev.filter((p) => p !== privilege)
                : [...prev, privilege]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isValidGmail(email)) {
            toast.error('Please enter a valid Gmail or LBA email address');
            return;
        }

        if (selectedPrivileges.length === 0) {
            toast.error('Please select at least one privilege');
            return;
        }

        setIsSubmitting(true);
        try {
            // Note: Clerk ID will be assigned when the user signs in for the first time
            // For now, we'll use a placeholder or handle this differently
            await createAdmin({
                email,
                clerkId: `pending_${email}`, // Placeholder - will be updated on first login
                privileges: selectedPrivileges,
                createdByAdminId: admin._id,
            });

            toast.success('Admin created successfully');
            navigate('/admins');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="secondary" onClick={() => navigate('/admins')}>
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-3xl font-bold">Add Admin</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Create a new platform administrator
                    </p>
                </div>
            </div>

            <Card>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <Input
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@lba.me"
                        helperText="Must be a Gmail (@gmail.com) or LBA (@lba.me) address"
                        required
                    />

                    <div>
                        <label className="block text-sm font-medium mb-3">
                            Privileges <span className="text-red">*</span>
                        </label>
                        <div className="space-y-3">
                            {PRIVILEGES.map((privilege) => (
                                <Switch
                                    key={privilege}
                                    checked={selectedPrivileges.includes(privilege)}
                                    onChange={() => togglePrivilege(privilege)}
                                    label={getPrivilegeLabel(privilege)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/admins')}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={isSubmitting}
                        >
                            Create Admin
                        </Button>
                    </div>
                </form>
            </Card>

            <Card title="Important Notes" className="bg-yellow/5 border-yellow">
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-light dark:text-muted-dark">
                    <li>The new admin will sign in using Clerk with their Gmail or LBA email</li>
                    <li>Only @gmail.com and @lba.me email domains are allowed</li>
                    <li>Super admin privileges cannot be assigned - only the original super admin has this access</li>
                    <li>You can modify privileges later from the admin detail page</li>
                </ul>
            </Card>
        </div>
    );
}

